/**
 * Small bits of state shared between the punch flow and the rest of the app, kept at
 * module level so code outside React (the service-worker updater in main.tsx) can use it.
 */

// --- Is a clock-in/out flow open? -------------------------------------------
//
// An app update must never reload the page while the cleaner is scanning, waiting for
// GPS or sending a punch (it would lose the flow and abort the request).

let openFlows = 0;
const closedListeners = new Set<() => void>();

/** Marks a punch flow as open. Call the returned function when it closes. */
export function markPunchFlowOpen(): () => void {
  openFlows++;
  let closed = false;
  return () => {
    if (closed) return;
    closed = true;
    openFlows = Math.max(0, openFlows - 1);
    if (openFlows === 0) for (const fn of [...closedListeners]) fn();
  };
}

export function isPunchFlowOpen(): boolean {
  return openFlows > 0;
}

/** Called each time the last open punch flow closes. */
export function onPunchFlowsClosed(fn: () => void): () => void {
  closedListeners.add(fn);
  return () => {
    closedListeners.delete(fn);
  };
}

// --- Site names for stickers scanned before -----------------------------------
//
// A sticker only carries a token; the site name comes back from the server. Remember it
// so that a later clock-in with the same sticker and no signal can still say where.

const STICKER_KEY = "clockin.stickerSites";
const MAX_STICKERS = 50;

function readStickers(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(STICKER_KEY) ?? "{}");
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function rememberStickerSite(token: string, siteName: string): void {
  try {
    const all = readStickers();
    delete all[token];
    all[token] = siteName; // newest last
    const keys = Object.keys(all);
    for (const k of keys.slice(0, Math.max(0, keys.length - MAX_STICKERS))) delete all[k];
    localStorage.setItem(STICKER_KEY, JSON.stringify(all));
  } catch {
    // Display only: nothing lost.
  }
}

export function stickerSiteName(token: string): string | null {
  const name = readStickers()[token];
  return typeof name === "string" ? name : null;
}
